import { alpha } from "@mui/material/styles";

import { type ComponentOverrides } from "theme/theme.types";

import { sizeVariants } from "theme/theme.helpers";

import { borderRadius } from "design-tokens";

const SWITCH_THUMB_INSET = 2;

/**
 * The ring MUI's hover overlay and ripple live in — they sit on `switchBase` sized
 * `thumb + 2 * padding`, so without this the halo is a 2px sliver. Also the root's padding, whose
 * `overflow: hidden` would otherwise cut it. **5 is the floor:** the focus ring is a 2px outline at a
 * 2px offset, so below that the same `overflow: hidden` slices it (`docs/ui-context.md` §5).
 */
const SWITCH_AFFORDANCE_PADDING = 6;

/** Figma's `switch/focusRipple/hovered`, `/focused` and `/pressed`. */
const SWITCH_HOVERED_OPACITY = 0.08;
const SWITCH_FOCUSED_OPACITY = 0.16;
const SWITCH_PRESSED_OPACITY = 0.2;

/**
 * Figma draws the hover and focus halo 4px narrower than the press one — 28 against 32 at Medium, 24
 * against 28 at Small — and the press one is exactly `switchBase`. So press is `switchBase`'s own
 * `background-color`, and hover and focus are a circle inset from its edge. A gradient, not a border plus
 * `background-clip`: a border would shrink the box MUI positions the input and the ripple in.
 * `closest-side` makes one rule serve both sizes; the half-pixel either side of the edge stands in for the
 * antialiasing a hard colour stop does not get.
 */
export const SWITCH_HALO_INSET = 2;

const switchHalo = (color: string) =>
  `radial-gradient(circle closest-side, ${color} calc(100% - ${SWITCH_HALO_INSET + 0.5}px), transparent calc(100% - ${SWITCH_HALO_INSET - 0.5}px))`;

/**
 * Laid over MUI's ripple, which is kept. MUI's own hover tint fills the full 32px circle, so the hover rule
 * clears it rather than out-painting it. Press clears the gradient: it paints above `background-color`.
 */
const switchBaseStates = (tint: string) => ({
  // Before the focus and press rules: on equal specificity they must out-paint it.
  "&:hover": {
    backgroundColor: "transparent",
    backgroundImage: switchHalo(alpha(tint, SWITCH_HOVERED_OPACITY)),
    // A tap leaves `:hover` stuck on touch screens; `:active` still answers the press.
    "@media (hover: none)": {
      backgroundImage: "none",
    },
  },
  "&.Mui-focusVisible": {
    backgroundImage: switchHalo(alpha(tint, SWITCH_FOCUSED_OPACITY)),
  },
  "&:active": {
    backgroundColor: alpha(tint, SWITCH_PRESSED_OPACITY),
    backgroundImage: "none",
  },
});

/**
 * MUI builds its switch the other way round — a 34x14 track floating in a 58x38 root — so every
 * number here is a replacement, not a tweak.
 */
const SWITCH_SIZES = {
  medium: { trackWidth: 36, trackHeight: 20, thumbSize: 16 },
  small: { trackWidth: 28, trackHeight: 16, thumbSize: 12 },
} as const;

/**
 * Travel is *derived*, so no size can ship with a thumb that stops short of its edge. Root descendants
 * because MUI's own `size="small"` variant is one too and a `thumb` slot override would lose.
 */
const switchSizeVariants = sizeVariants(SWITCH_SIZES, (size) => {
  const { trackWidth, trackHeight, thumbSize } = SWITCH_SIZES[size];
  const pad = SWITCH_AFFORDANCE_PADDING;

  return {
    width: trackWidth + pad * 2,
    height: trackHeight + pad * 2,
    padding: pad,
    // Negated, so the affordance paints outside the control and costs nothing in layout.
    margin: -pad,

    "& .MuiSwitch-thumb": {
      width: thumbSize,
      height: thumbSize,
    },
    // `switchBase` is pinned to the *padding* box's corner, so its own padding carries the
    // affordance ring as well as the thumb inset.
    "& .MuiSwitch-switchBase": {
      padding: pad + SWITCH_THUMB_INSET,
      "&.Mui-checked": {
        transform: `translateX(${trackWidth - 2 * SWITCH_THUMB_INSET - thumbSize}px)`,
      },
    },
  };
});

/**
 * Figma `Toggle` (node `15148:17364`). The painted control is replaced wholesale; MUI's interaction
 * affordance is kept, which is what `SWITCH_AFFORDANCE_PADDING` is for.
 *
 * The **root is the affordance box, not the track** — `SwitchTrack` is a plain flow child at
 * `100%`, so the root's padding sizes and positions it, and its `overflow: hidden` is load-bearing
 * (it clips the input MUI stretches to `left: -100%; width: 300%`).
 *
 * **Everything is a descendant of `root`, deliberately.** `thumb` and `track` are their own styled
 * components, so a slot override carries one class where MUI reaches the same elements with two or
 * three — a `styleOverrides.thumb` of `width: 12` silently renders at 16.
 *
 * Figma draws the small unchecked thumb white and the medium one `background.default`; both ship as the
 * medium one, which reads as the intended value.
 */
export const MuiSwitch: ComponentOverrides["MuiSwitch"] = {
  styleOverrides: {
    root: ({ theme }) => ({
      variants: switchSizeVariants,

      "& .MuiSwitch-track": {
        borderRadius: borderRadius.full,
        backgroundColor: theme.palette.grey[500],
        // MUI fades the resting track to 0.38.
        opacity: 1,
      },
      "& .MuiSwitch-thumb": {
        // Painted directly rather than through `currentColor`, which stays MUI's ripple colour.
        backgroundColor: theme.palette.background.default,
        // MUI lifts the thumb with `shadows[1]`; the design draws it flat.
        boxShadow: "none",
      },
      "& .MuiSwitch-switchBase": switchBaseStates(theme.palette.common.white),
      "& .MuiSwitch-switchBase.Mui-checked": {
        ...switchBaseStates(theme.palette.primary.light),

        "& .MuiSwitch-thumb": {
          backgroundColor: theme.palette.background.paper,
        },
        "& + .MuiSwitch-track": {
          backgroundColor: theme.palette.primary.main,
          // MUI sets 0.5 from `switchBase`, out-ranking the resting `opacity: 1` above.
          opacity: 1,
        },
      },
      // After the checked block: at equal specificity disabled has to win. Figma's
      // `action/disabledBackground` thumb is `grey[100]` — the palette leaves `action` at MUI's defaults.
      "& .MuiSwitch-switchBase.Mui-disabled": {
        "& .MuiSwitch-thumb": {
          backgroundColor: theme.palette.grey[100],
        },
        "& + .MuiSwitch-track": {
          backgroundColor: theme.palette.grey[300],
          opacity: 1,
        },
      },
      // Not in Figma, whose focused cell is the halo alone — about 1.1:1 against white. On the
      // **track**, not the root, which is the affordance box and would trace a rectangle around the
      // pill. An `outline` rather than a shadow: the root's `overflow: hidden` clips one.
      "&:has(.Mui-focusVisible) .MuiSwitch-track": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: SWITCH_THUMB_INSET,
      },
    }),
  },
};
